import { Component, ElementRef, Inject, OnInit, Renderer2, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LoadingService } from '../../../../shared/loading/loading.service';
import { DropboxChooserService } from '../../../../base/services/dropbox-chooser/dropbox-chooser.service';
import { GooglePickerService } from '../../../../base/services/google-picker/google-picker.service';
import { FilesUploadService } from '../../services/files-upload-service/files-upload.service';
import { FileUploadType } from '../../../../models/files-upload';

@Component({
  selector: 'app-files-upload-dialog',
  templateUrl: './files-upload-dialog.component.html',
  styleUrls: ['./files-upload-dialog.component.scss'],
})
export class FilesUploadDialogComponent implements OnInit {

  /**
   * Object for the ElementRef fileinput
   */
  @ViewChild('fileInput') fileInput: ElementRef;

  /**
   * Object for ElementRef container
   */
  @ViewChild('container') container: ElementRef;

  /**
   * New array to store the documents with multiple selection
   */
  newArray = [];

  /**
   * Extension of documents
   */
  extensions: string;

  /**
   * Language of the picker
   */
  language: string;

  /**
   * Allow dropbox
   */
  allowDropbox: boolean

  _transParam = false;

  minPage: number = 1;

  isValid: boolean = true;

  isCreate: boolean = false;

  type: string;

  constructor(
    public dialogRef: MatDialogRef<FilesUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly picker: GooglePickerService,
    private readonly chooser: DropboxChooserService,
    private readonly loading: LoadingService,
    private readonly renderer: Renderer2,
    private readonly filesUpload: FilesUploadService) {
    this.extensions = data.extensions;
    this.type = data.type;
  }

  ngOnInit(): void {
    this.isCreate = this.filesUpload.getCreate();
    this.allowDropbox = this.chooser.isSupported();
    this.minPage = this.filesUpload.getMinPage();
  }

  loadDropbox(): void {
    if (!this.allowDropbox) {
      return
    } else {
      this.setDropboxConfig();
      this.chooser.loadDropbox();
      this.chooser.finish.subscribe((res) => {
        if (res.length) {
          this.loading.set(true);
          for (let i = 0; i < res.length; i++) {
            this.chooser.proceesFile(res[i].link).then((response) => {
              const file = new File([response], res[i].name, { type: response.type });
              const obj = { doc: file, type: 'external' }
              this.newArray.push(obj);
              if (res.length === this.newArray.length) {
                this.validatorFile(file, res)
              }
            });
          }
          this.listenValidatorFile(this.newArray, 'external');
        }
      });
    }
  }

  validatorFile(file: File, res: unknown[]): void {
    if (this.type === FileUploadType.PRIMARY) {
      let index = 0;
      this.filesUpload.resetValidation();
      this.filesUpload.validateFile(file, 1, res.length, index);
      index++;
    } else {
      this.dialogRef.close(this.newArray);
      this.loading.set(false);
    }
  }

  loadGoogleDrive(): void {
    this.setPickerConfig();
    this.picker.initClient((data) => {
      if (data.action === 'picked') {
        this.loading.set(true);
        this.newArray = [];
        for (let i = 0; i < data.docs.length; i++) {
          const doc = data.docs[i];
          const fileName = doc.name;
          this.picker.proceesFile(data.docs[i].id).then((response) => {
            const file = new File([response], fileName, { type: response.type });
            const obj = { doc: file, type: 'external' }
            this.newArray.push(obj);
            if (data.docs.length === this.newArray.length) {
              if (this.type === FileUploadType.PRIMARY) {
                let index = 0;
                this.filesUpload.resetValidation();
                this.filesUpload.validateFile(file, 1, data.docs.length, index);
                index++;
              } else {
                this.dialogRef.close(this.newArray);
                this.loading.set(false);
              }
            }
          })
        }
        this.listenValidatorFile(this.newArray, 'external');
      }
    });
  }

  loadLocalFile(event: any): void {
    if (this.type === FileUploadType.PRIMARY) {
      const files = event.target.files
      this.filesUpload.resetValidation();
      let index = 0;
      for (const file of files) {
        this.filesUpload.validateFile(file, 1, files.length, index);
        index++;
      }
      this.listenValidatorFile(files, 'local', event);
    } else {
      this.dialogRef.close(event);
    }
  }

  setPickerConfig(): void {
    this.language = this.getLanguage() || 'es';
    this.picker.setPickerConfig(this.extensions, this.language);
  }

  setDropboxConfig(): void {
    this.chooser.setDropboxConfig(this.extensions);
  }

  getLanguage(): string {
    const language = 'lang=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(language) == 0) {
        return c.substring(language.length, c.length);
      }
    }

    return '';
  }

  onDragEnter(): void { }

  /**
   * The function register the event onDragLeave
   * @param {event} event The event generated by the action
   */
  onDragLeave(event: any): void {
    this.renderer.removeClass(this.container.nativeElement, 'drogover');
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * The function register the event onDrop
   * @param {event} event The event generated by the action
   */
  onDrop(event: any): void {
    this.renderer.removeClass(this.container.nativeElement, 'drogover');
    event.stopPropagation();
    event.preventDefault();
    //@ts-ignore
    const files = [...event.dataTransfer.files];
    if (files.length > 0) {
      const draggedFile = [];
      const obj = {
        event: event,
        type: 'drag',
        doc: files
      }
      if (this.type === FileUploadType.PRIMARY) {
        let index = 0;
        this.filesUpload.resetValidation();
        for (const file of files) {
          this.filesUpload.validateFile(file, 1, files.length, index);
          index++;
        }
        this.listenValidatorFile(draggedFile, 'drag', obj);
      } else {
        draggedFile.push(obj)
        this.dialogRef.close(draggedFile);
      }
    }
  }

  /**
   * The function register the event onDragOver
   * @param {event} event The event generated by the action
   */
  onDragOver(event: any): void {
    this.renderer.addClass(this.container.nativeElement, 'drogover');
    event.stopPropagation();
    event.preventDefault();
  }

  listenValidatorFile(draggedFile: unknown[], type: string, obj?: unknown): void {
    this.filesUpload.blockUpload.subscribe((event: any) => {
      if (event) {
        this.isValid = true;
        if (type === 'drag') {
          draggedFile.push(obj)
          this.dialogRef.close(draggedFile);
        } else if (type === 'external') {
          this.dialogRef.close(this.newArray);
          this.loading.set(false);
        } else {
          this.dialogRef.close(obj);
        }
      } else {
        this.isValid = false;
        this.loading.set(false);
      }
    })
  }

}
